import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RENTAL } from "@/constants/testIds";

export const FilterPanel = ({ filters, setFilters, sort, setSort, onClear }) => {
  const update = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

  return (
    <aside
      data-testid={RENTAL.filterPanel}
      className="sticky top-28 bg-white/70 backdrop-blur-md rounded-3xl border border-stone-200 p-6 shadow-[0_8px_32px_rgba(28,25,23,0.04)]"
    >
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="overline">Refine</p>
          <h2 className="font-heading text-2xl font-medium tracking-tight mt-1">
            Filters
          </h2>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClear}
          data-testid={RENTAL.filterClear}
          className="text-stone-500 hover:text-stone-800"
        >
          Reset
        </Button>
      </div>

      <div className="space-y-5">
        <div>
          <Label className="overline">Monthly Rent</Label>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <Input
              type="number"
              placeholder="Min"
              data-testid={RENTAL.filterMinRent}
              value={filters.min_rent ?? ""}
              onChange={(e) =>
                update("min_rent", e.target.value === "" ? "" : Number(e.target.value))
              }
              className="bg-white"
            />
            <Input
              type="number"
              placeholder="Max"
              data-testid={RENTAL.filterMaxRent}
              value={filters.max_rent ?? ""}
              onChange={(e) =>
                update("max_rent", e.target.value === "" ? "" : Number(e.target.value))
              }
              className="bg-white"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="overline">Min Beds</Label>
            <Input
              type="number"
              step="0.5"
              placeholder="—"
              data-testid={RENTAL.filterMinBeds}
              value={filters.min_bedrooms ?? ""}
              onChange={(e) =>
                update(
                  "min_bedrooms",
                  e.target.value === "" ? "" : Number(e.target.value),
                )
              }
              className="bg-white mt-2"
            />
          </div>
          <div>
            <Label className="overline">Min Baths</Label>
            <Input
              type="number"
              step="0.5"
              placeholder="—"
              data-testid={RENTAL.filterMinBaths}
              value={filters.min_bathrooms ?? ""}
              onChange={(e) =>
                update(
                  "min_bathrooms",
                  e.target.value === "" ? "" : Number(e.target.value),
                )
              }
              className="bg-white mt-2"
            />
          </div>
        </div>

        <div>
          <Label className="overline">Min Sqft</Label>
          <Input
            type="number"
            placeholder="—"
            data-testid={RENTAL.filterMinSqft}
            value={filters.min_sqft ?? ""}
            onChange={(e) =>
              update("min_sqft", e.target.value === "" ? "" : Number(e.target.value))
            }
            className="bg-white mt-2"
          />
        </div>

        <div>
          <Label className="overline">City</Label>
          <Input
            placeholder="e.g. Austin"
            data-testid={RENTAL.filterCity}
            value={filters.city ?? ""}
            onChange={(e) => update("city", e.target.value)}
            className="bg-white mt-2"
          />
        </div>

        <div className="h-px bg-stone-200 my-2" />

        <ToggleRow
          label="Garage"
          checked={!!filters.garage}
          onChange={(v) => update("garage", v)}
          testid={RENTAL.filterGarage}
        />
        <ToggleRow
          label="Parking"
          checked={!!filters.parking}
          onChange={(v) => update("parking", v)}
          testid={RENTAL.filterParking}
        />
        <ToggleRow
          label="Pet Friendly"
          checked={!!filters.pet_friendly}
          onChange={(v) => update("pet_friendly", v)}
          testid={RENTAL.filterPet}
        />
        <ToggleRow
          label="Furnished"
          checked={!!filters.furnished}
          onChange={(v) => update("furnished", v)}
          testid={RENTAL.filterFurnished}
        />
        <ToggleRow
          label="Favorites Only"
          checked={!!filters.favorite}
          onChange={(v) => update("favorite", v)}
          testid={RENTAL.filterFavorites}
          accent
        />

        <div className="h-px bg-stone-200 my-2" />

        <div>
          <Label className="overline">Sort By</Label>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger
              className="mt-2 bg-white"
              data-testid={RENTAL.sortSelect}
            >
              <SelectValue placeholder="Most recent" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">Most recent</SelectItem>
              <SelectItem value="rent_asc">Rent: Low → High</SelectItem>
              <SelectItem value="rent_desc">Rent: High → Low</SelectItem>
              <SelectItem value="beds_desc">Bedrooms: Most</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </aside>
  );
};

const ToggleRow = ({ label, checked, onChange, testid, accent }) => (
  <div className="flex items-center justify-between">
    <span
      className={`text-sm font-medium ${accent ? "text-[#C25934]" : "text-stone-700"}`}
    >
      {label}
    </span>
    <Switch
      data-testid={testid}
      checked={checked}
      onCheckedChange={onChange}
      className="data-[state=checked]:bg-[#4A5D4E]"
    />
  </div>
);

export default FilterPanel;
